// Estilos do menu lateral (drawer/navegação global) da modernização do
// InfoChat (Entrega M4).
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (idealmente já com `theme.infoChat` da Entrega
// M2) e devolve só objetos de estilo simples (CSS-in-JS), para serem
// mesclados nas classes já existentes de `layout/index.js` e
// `layout/MainListItems.js`. Não aplica nada sozinha — quem decide onde/
// como usar é cada um desses dois arquivos.
//
// Escopo desta entrega: só o drawer/menu (superfícies, bordas, textos,
// divisores, submenus, rodapé, estados hover/ativo e foco visível). O
// AppBar/Toolbar (cabeçalho) permanece fora daqui, por decisão explícita —
// ver comentário equivalente em `infoChatGlobalShellStyles.js` (M3).
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor — nunca um hex
// literal novo aqui. A única exceção é `"#fff"` como cor do ícone sobre o
// círculo ativo/hover (contraste fixo sobre `tokens.brand.primaryColor`),
// que já existia em `MainListItems.js` antes desta entrega e não tem
// token equivalente no sistema de tokens da M2 — não é uma cor nova
// introduzida agora, só migrada para este helper.

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

// Este helper não depende de breakpoints/spacing do MUI (o menu usa a
// mesma composição visual em qualquer largura — só o container muda entre
// `layout/index.js`), então, ao contrário de `infoChatLoginStyles.js`, não
// precisa de um tema "seguro" para essas utilidades — só dos tokens de cor
// abaixo.
const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing &&
      tokens.status
  );

// Mesmo padrão de fallback seguro já usado em infoChatGlobalShellStyles.js
// (M3) e infoChatLoginStyles.js (LOGIN-2) — nunca deixa o menu quebrar caso
// theme.infoChat esteja ausente/incompleto, e nunca diverge dos tokens
// reais por reaproveitar a mesma função pura da M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos do menu lateral modernizado (drawer/navegação global).
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 */
export const createInfoChatNavigationStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // Superfície do próprio drawer (painel do menu) — aberto ou recolhido.
    drawerSurface: {
      backgroundColor: tokens.surfaces.base,
      borderRight: `1px solid ${tokens.borders.subtle}`,
      boxShadow: tokens.shadows.sm
    },

    // Faixa do topo do drawer (onde fica a logo + botão de recolher).
    drawerHeader: {
      backgroundColor: tokens.surfaces.base,
      borderBottom: `1px solid ${tokens.borders.subtle}`
    },

    // Texto padrão dos itens do menu (rótulo).
    listItemText: {
      color: tokens.text.secondary
    },

    // Avatar circular do ícone de cada item — estado padrão (inativo).
    iconAvatar: {
      backgroundColor: tokens.surfaces.subtle,
      color: tokens.text.secondary
    },

    // Avatar circular do ícone — estado ativo/hover.
    iconAvatarActive: {
      backgroundColor: tokens.brand.primaryColor,
      // Contraste fixo sobre a cor institucional — ver nota no topo do
      // arquivo sobre esta ser a única exceção a "nunca hex literal novo".
      color: "#fff",
      boxShadow: `0 4px 12px ${tokens.brand.primaryColor}30`
    },

    // Cor do badge de alerta (ex.: conexão offline) — mesmo vermelho
    // semântico já usado nos indicadores de status da M2
    // (`tokens.status.alert`), em vez de um hex isolado só para o menu.
    badgeAlert: {
      backgroundColor: tokens.status.alert.main
    },

    // Fundo sutil ("pill") por trás da linha inteira do item ativo.
    activeItemSurface: {
      backgroundColor: tokens.mode === "dark" ? tokens.surfaces.raised : `${tokens.brand.primaryColor}18`,
	  borderColor: tokens.mode === "dark" ? tokens.borders.strong : `${tokens.brand.primaryColor}30`
    },

    // Fundo sutil por trás da linha inteira no hover (mais discreto que o
    // do item ativo).
    hoverItemSurface: {
      backgroundColor: `${tokens.brand.primaryColor}08`
    },

    // Barra de destaque à esquerda do item ativo — `inset box-shadow` para
    // nunca deslocar o conteúdo (evita ter que compensar padding).
    activeItemAccent: {
      boxShadow: `inset 3px 0 0 ${tokens.brand.primaryColor}`
    },

    // Anel de foco visível (navegação real por teclado) — mesma cor
    // institucional. Usa `outline`/`outlineOffset`, nunca `boxShadow`: o
    // item ativo já usa `boxShadow` para a barra de 3px (activeItemAccent
    // acima), e as duas propriedades disputariam o mesmo slot se ambas
    // usassem `boxShadow` — um item ativo E focado por teclado ficaria só
    // com uma das duas indicações visíveis. `outline` é uma propriedade
    // CSS totalmente independente de `boxShadow` (nunca afeta layout,
    // mesmo com offset negativo), então os dois estados sempre coexistem:
    // a barra ativa (boxShadow) e o anel de foco (outline) juntos.
    focusRingAccent: {
      outline: `2px solid ${tokens.brand.primaryColor}`,
      outlineOffset: "-2px"
    },

    // Fundo dos submenus expandidos (Gerenciamento/Campanhas/Flowbuilder).
    submenuSurface: {
      backgroundColor: tokens.surfaces.subtle
    },

    // Divisores entre seções do menu.
    divider: {
      backgroundColor: tokens.borders.subtle
    },

    // Cabeçalho de seção (ex.: "Administração").
    subheaderText: {
      color: tokens.brand.primaryColor
    },

    // Rodapé com o número da versão.
    versionText: {
      color: tokens.text.secondary,
      borderTop: `1px solid ${tokens.borders.subtle}`
    }
  };
};

export default createInfoChatNavigationStyles;
